// ─── Player Controller ────────────────────────────────────────────────────────

import {
  Direction,
  PLAYER_SPEED,
  TILE_SIZE,
  type Position,
} from "./constants";
import type { Character } from "./character";

/**
 * PlayerController reads keyboard state and moves the player character,
 * checking collision against a boolean grid.
 */
export class PlayerController {
  /**
   * Update the player character based on currently pressed keys.
   *
   * @param _dt - Delta time (unused; movement is per-frame at fixed speed)
   * @param character - The player's Character instance
   * @param keysPressed - Set of currently pressed key strings
   * @param collisionMap - 2D boolean grid (true = blocked)
   */
  update(
    _dt: number,
    character: Character,
    keysPressed: Set<string>,
    collisionMap: boolean[][],
  ): void {
    let dx = 0;
    let dy = 0;

    // Read directional input: WASD + Arrow keys
    if (keysPressed.has("ArrowUp") || keysPressed.has("w") || keysPressed.has("W")) {
      dy = -PLAYER_SPEED;
      character.direction = Direction.Up;
    }
    if (keysPressed.has("ArrowDown") || keysPressed.has("s") || keysPressed.has("S")) {
      dy = PLAYER_SPEED;
      character.direction = Direction.Down;
    }
    if (keysPressed.has("ArrowLeft") || keysPressed.has("a") || keysPressed.has("A")) {
      dx = -PLAYER_SPEED;
      character.direction = Direction.Left;
    }
    if (keysPressed.has("ArrowRight") || keysPressed.has("d") || keysPressed.has("D")) {
      dx = PLAYER_SPEED;
      character.direction = Direction.Right;
    }

    // Normalize diagonal movement
    if (dx !== 0 && dy !== 0) {
      const factor = Math.SQRT1_2; // 1/sqrt(2)
      dx *= factor;
      dy *= factor;
    }

    character.isMoving = dx !== 0 || dy !== 0;

    if (!character.isMoving) return;

    // Try X movement
    const newX = character.position.x + dx;
    if (!this.checkCollision(newX, character.position.y, character, collisionMap)) {
      character.position.x = newX;
    }

    // Try Y movement
    const newY = character.position.y + dy;
    if (!this.checkCollision(character.position.x, newY, character, collisionMap)) {
      character.position.y = newY;
    }
  }

  /**
   * Check if moving to (x, y) would cause a collision.
   * Tests the character's foot hitbox against the collision grid.
   */
  private checkCollision(
    x: number,
    y: number,
    character: Character,
    collisionMap: boolean[][],
  ): boolean {
    const hitbox = character.getHitbox();
    // Adjust hitbox to proposed position
    const hx = x + (hitbox.x - character.position.x);
    const hy = y + (hitbox.y - character.position.y);

    const rows = collisionMap.length;
    if (rows === 0) return false;
    const cols = collisionMap[0].length;

    // Check all grid cells the hitbox overlaps
    const startCol = Math.floor(hx / TILE_SIZE);
    const endCol = Math.floor((hx + hitbox.width - 1) / TILE_SIZE);
    const startRow = Math.floor(hy / TILE_SIZE);
    const endRow = Math.floor((hy + hitbox.height - 1) / TILE_SIZE);

    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        // Out of bounds = blocked
        if (row < 0 || row >= rows || col < 0 || col >= cols) {
          return true;
        }
        if (collisionMap[row][col]) {
          return true;
        }
      }
    }

    return false;
  }
}
